<template>
  <div v-if="editRow" ref="containerRef" @mouseup.stop @mousedown.stop>
    <UIInputSelect
      v-model="currentValue"
      :data
      size="sm"
      @ready="handleReady"
    />
  </div>
  <span v-else class="repository-collaborators-access" @click="handleClickEdit">
    <div class="repository-collaborators-access__value">
      <div>{{ displayValue || EMPTY_VALUE_PLACEHOLDER }}</div>
      <template v-if="assetsCount">
        <button
          v-if="isEditable"
          v-tooltip.top="tooltip"
          type="button"
          class="ui-link"
          @click.stop="handleShareAssets"
        >{{ assetsCount }} asset{{ assetsCount > 1 ? 's' : '' }}</button>
        <div
          v-else
          v-tooltip.top="tooltip"
        >{{ assetsCount }} asset{{ assetsCount > 1 ? 's' : '' }}</div>
      </template>
    </div>
    <UIPreloader v-if="isLoading" />
    <PencilIcon v-else-if="isEditable" aria-hidden="true" />
  </span>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, useTemplateRef, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'

import { RepositoryCollaborator, RepositoryStatus } from '@types'

import { ACCESS_TYPE_OWNER, ACCESS_TYPES, ASSET_FIELD, EMPTY_VALUE_PLACEHOLDER, UNKNOWN_VALUE } from '@/const'

import { handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'
import { useRepositoriesStore } from '@/store/repositories'

import { UIPreloader } from '@ui/core'
import { UIInputSelect } from '@ui/fields'
import { PencilIcon } from '@heroicons/vue/24/outline'
import CollaboratorsAssetsTree from './CollaboratorsAssetsTree.vue'

type Props = {
  item: RepositoryCollaborator
  editRow: boolean
  isReadonly: boolean
  repositoryId: string
}

type Emits = {
  (e: 'edit', value?: RepositoryCollaborator): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()
const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()
const repositoriesStore = useRepositoriesStore()

const { success } = useNotifications()

const containerRef = useTemplateRef('containerRef')

const currentValue = ref<string>()

const isLoading = ref(false)

const displayValue = computed(() => {
  return ACCESS_TYPES[props.item.access_type - 1] || EMPTY_VALUE_PLACEHOLDER
})

const assetsCount = computed(() => props.item.share_root_asset_ids?.length)

const isCurrentRepository = computed(() => repositoriesStore.getRepository?.id === props.repositoryId)

const tooltip = computed(() => {
  if (!isCurrentRepository.value) return
  const content = props.item.share_root_asset_ids
    ?.map(asset => assetsBunchStore.getElementById(asset)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE) || []
  return {
    content: content.join('<br />'),
    html: true,
  }
})

const data = computed(() =>
  ACCESS_TYPES.filter((_, index) => index !== ACCESS_TYPE_OWNER - 1).map(
    (label, index) => ({
      value: `${index + 1}`,
      label,
    }),
  ),
)

const isEditable = computed(() => {
  return (
    !props.isReadonly &&
    props.item.access_type !== ACCESS_TYPE_OWNER &&
    props.item.status !== RepositoryStatus.PENDING
  )
})

const handleReady = (el: HTMLInputElement) => {
  el.focus()
}

const handleClickEdit = async () => {
  if (!isEditable.value) return
  currentValue.value = `${props.item.access_type}`
  emit('edit', props.item)
}

const handleUpdateAccessType = async () => {
  if (!currentValue.value) return
  const access_type = +currentValue.value
  emit('edit', undefined)
  currentValue.value = undefined
  await handleUpdate({ access_type }, () => {
    success({ message: 'Access rights have been successfully changed' })
  })
}

const handleUpdate = async (data: Partial<RepositoryCollaborator>, callback?: () => void) => {
  if (!props.repositoryId || !props.item.user?.id) return
  const id = props.item.user.id
  try {
    isLoading.value = true
    await repositoriesCollaboratorsStore.update(id, props.repositoryId, data)
    callback?.()
  } catch (e) {
    handleCatchedError(e as string, { id })
  } finally {
    isLoading.value = false
  }
}

const handleShareAssets = (e: MouseEvent) => {
  if (!isEditable.value) return
  e.stopPropagation()
  const modal = modalsStore.init('collaborators-assets-tree', markRaw(CollaboratorsAssetsTree))
  modal?.open(modalsStore.getZIndex(), {
    assetIds: props.item.share_root_asset_ids,
    repositoryId: props.repositoryId,
    onDone: (value: string[]) => {
      handleUpdate({  share_root_asset_ids: value }, () => {
        success({ message: value.length ? 'Assets have been successfully shared' : 'You successfully shared the whole repository' })
      })
    },
  })
}

watch(currentValue, (value, oldValue) => {
  if (!oldValue || !value) return
  handleUpdateAccessType()
})

onClickOutside(containerRef, event => {
  const path =
    (event as any).path || (event.composedPath && event.composedPath())
  if (
    path.find((item: HTMLElement) =>
      item.classList?.contains('v-popper__popper'),
    )
  )
    return
  emit('edit', undefined)
  currentValue.value = undefined
})
</script>

<style scoped>
.repository-collaborators-access {
  @apply flex items-center;
  @apply gap-1;

  svg {
    @apply w-3 h-3;
    @apply hidden;
    @apply hover:text-indigo-500;
  }

  &__value {
    @apply flex flex-col items-start;
  }
}

[data-column='access_type']:hover {
  .repository-collaborators-access {
    svg {
      @apply block;
    }
  }
}
</style>
